import {
    Box,
    BoxProps,
    IconButton,
    InternalStandardProps,
    List,
    ListItem,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Layout } from "../Layout";
import { useNavigate } from "react-router-dom";
import { useRouting } from "JS/React/Hooks/Routes";
import { AppHeader } from "JS/React/Components/AppHeader";
import { useGlobalNavStack } from "JS/React/Hooks/NavStack";
import { AppTypography } from "JS/React/Components/AppTypography";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AppDivider } from "JS/React/Components/AppDivider";
import { useThemeModeContext } from "JS/React/Context/ThemeModeProvider";
import { useDashboardPushStatus } from "JS/React/Hooks/Firebase/GiftDashboard";
import { messaging } from "JS/Helpers/UserMessaging";
import { AppCircularProgress } from "JS/React/Components/Progress/AppCircularProgress";
import { AppSwitch } from "JS/React/Components/AppSwitch";
import { getUserName, isBetaEnvironment, isStageEnvironment } from "JS/Helpers";
import { config } from "JS/Config";
import { getLocalFcmToken, getWebcastUUID } from "JS/Helpers/PushNotifications";
import { registerEndpoint } from "JS/Services/pinPointService/pinpointEndpointService";
import { useGlobalMediaEssentials } from "JS/React/Hooks/MediaEssentials";
import { ensurePinpointKeys } from "JS/Helpers/PinpointKeys";

const AppSettingsRoot = styled(Box)({});

const Grey600Text = styled(AppTypography)(({ theme }) => ({
    color: `${theme.palette.grey[600]} !important`,
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
    fontWeight: 600,
    display: "flex",
    justifyContent: "flex-start",
    backgroundColor: `${theme.palette.background} !important`,
}));

const NotificationSettingsItem = styled(ListItem)(({ theme }) => ({
    fontWeight: 600,
    display: "flex",
    justifyContent: "space-between",
    backgroundColor: `${theme.palette.background} !important`,
}));

const StyledList = styled(List)({
    flexGrow: 1,
});

const ListHeader = styled(Grey600Text)({
    fontWeight: 800,
});

const ListText = styled(Grey600Text)({
    fontWeight: 600,
});

const ListParagraphText = styled(Grey600Text)({
    fontWeight: 400,
});

interface SelectableListItemProps {
    isSelected?: boolean;
}

const SelectableListItem = styled(StyledListItem, {
    shouldForwardProp: (prop) => prop !== "isSelected",
})<SelectableListItemProps>(({ theme, isSelected }) => ({
    ...(isSelected && {
        backgroundColor:
            theme.palette.mode === "light"
                ? `${theme.palette.grey[200]} !important`
                : `${theme.palette.grey[900]} !important`,
    }),
}));

const TopMarginHeader = styled(ListHeader)({
    marginTop: "10px",
});

export const Appsettings = (props: AppInfoProps) => {
    const { className, ...rest } = props;
    const { homeStack } = useGlobalNavStack();
    const navigate = useNavigate();
    const { linkProvider } = useRouting();
    const themeMode = useThemeModeContext();
    const { mediaEssentials } = useGlobalMediaEssentials();
    const { setPushStatus, loading, status, error, statusLoading } =
        useDashboardPushStatus();
    const pushSwitchChange = async () => {
        if (mediaEssentials?.enable_pinpoint) {
            await ensurePinpointKeys();
            const userId = config.user.memberId;
            const deviceId = getWebcastUUID();
            const deviceToken = getLocalFcmToken();
            localStorage.setItem(
                "NotificationStatus",
                !status ? "true" : "false",
            );
            registerEndpoint(userId, deviceId, deviceToken, "GCM", !status);
        }
        setPushStatus(!status);
    };

    return (
        <Layout>
            <AppSettingsRoot
                {...rest}
                className={className}
                display={"flex"}
                flexDirection={"column"}
                height={"100%"}
            >
                <AppHeader
                    title={"Settings"}
                    canGoBack
                    searchable={false}
                    onBackClick={() => {
                        if (!homeStack?.isHomeTrack) {
                            navigate(linkProvider.react.more().index());
                        } else {
                            navigate(linkProvider.react.home());
                        }
                    }}
                />
                {(isBetaEnvironment() || isStageEnvironment()) && (
                    <StyledList>
                        <ListHeader variant="h6">
                            User Information
                        </ListHeader>
                        <AppDivider />
                        <StyledListItem>
                            <ListText variant="h6">
                                {`${getUserName()} (${config.user.memberId})`}
                            </ListText>
                        </StyledListItem>
                        <AppDivider />
                    </StyledList>
                )}
                <StyledList>
                    <ListHeader variant="h6">
                        Dark Mode Settings
                    </ListHeader>
                    <AppDivider />
                    <SelectableListItem
                        isSelected={themeMode.mode === "light" && !themeMode.defaultSystem}
                        onClick={() =>
                            themeMode.toggleColorMode("light", false)
                        }
                    >
                        <IconButton
                            sx={{ mr: 1 }}
                            color="inherit"
                            style={{ display: "block" }}
                        >
                            {themeMode.mode === "light" &&
                            !themeMode.defaultSystem ? (
                                <FontAwesomeIcon
                                    style={{
                                        fontSize: "30px",
                                        color: "inherit",
                                    }}
                                    icon={["fas", "sun"]}
                                />
                            ) : (
                                <FontAwesomeIcon
                                    style={{
                                        fontSize: "30px",
                                        color: "inherit",
                                    }}
                                    icon={["far", "sun"]}
                                />
                            )}
                        </IconButton>
                        <ListText variant="h6">
                            Light
                        </ListText>
                    </SelectableListItem>
                    <AppDivider />
                    <SelectableListItem
                        isSelected={themeMode.mode === "dark" && !themeMode.defaultSystem}
                        onClick={() => themeMode.toggleColorMode("dark", false)}
                    >
                        <IconButton
                            sx={{ mr: 1 }}
                            color="inherit"
                            style={{ display: "block" }}
                        >
                            {themeMode.mode === "dark" &&
                            !themeMode.defaultSystem ? (
                                <FontAwesomeIcon
                                    style={{
                                        fontSize: "30px",
                                        color: "inherit",
                                    }}
                                    icon={["fas", "moon"]}
                                />
                            ) : (
                                <FontAwesomeIcon
                                    style={{
                                        fontSize: "30px",
                                        color: "inherit",
                                    }}
                                    icon={["far", "moon"]}
                                />
                            )}
                        </IconButton>
                        <ListText variant="h6">
                            Dark
                        </ListText>
                    </SelectableListItem>
                    <AppDivider />
                    <SelectableListItem
                        isSelected={themeMode.defaultSystem}
                        onClick={themeMode.toogleSystemMode}
                    >
                        <IconButton sx={{ mr: 1 }} color="inherit">
                            {themeMode.defaultSystem ? (
                                <FontAwesomeIcon
                                    style={{
                                        fontSize: "30px",
                                        color: "inherit",
                                    }}
                                    icon={["fas", "tablet"]}
                                />
                            ) : (
                                <FontAwesomeIcon
                                    style={{
                                        fontSize: "30px",
                                        color: "inherit",
                                    }}
                                    icon={["fas", "tablet-alt"]}
                                />
                            )}
                        </IconButton>
                        <Box>
                            <ListText variant="h6">
                                Use System Settings
                            </ListText>
                            <ListParagraphText variant="body1">
                                We'll adjust your appearance based on your
                                device's system settings.
                            </ListParagraphText>
                        </Box>
                    </SelectableListItem>
                    <AppDivider />
                    <TopMarginHeader variant="h6">
                        Gift Dashboard
                    </TopMarginHeader>
                    <AppDivider />
                    <NotificationSettingsItem>
                        {loading ? (
                            <AppCircularProgress />
                        ) : (
                            <>
                                <ListText>
                                    {error
                                        ? `${messaging.common.error}`
                                        : "Notification Settings"}
                                </ListText>
                                {!error &&
                                    (statusLoading ? (
                                        <AppCircularProgress />
                                    ) : (
                                        <AppSwitch
                                            checked={status}
                                            onSwitchChange={pushSwitchChange}
                                        />
                                    ))}
                            </>
                        )}
                    </NotificationSettingsItem>
                    <AppDivider />
                </StyledList>
            </AppSettingsRoot>
        </Layout>
    );
};

export interface AppInfoProps extends InternalStandardProps<BoxProps> {}
